import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { FiEdit2, FiEye, FiPlus, FiSend, FiSlash } from 'react-icons/fi';
import { RichContent } from '../../../components/announcements/RichContent';
import { Button } from '../../../components/ui/Button';
import { confirmDialog } from '../../../components/ui/ConfirmDialog';
import { Input } from '../../../components/ui/Input';
import { Modal } from '../../../components/ui/Modal';
import { toast } from '../../../components/ui/Toast';
import {
    adminApi,
    type AdminPlan,
    type Announcement,
    type AnnouncementListItem,
    type AnnouncementRecord,
    type AnnouncementStatus,
} from '../../../services/admin';
import styles from '../Admin.module.css';
import tabStyles from './AnnouncementsTab.module.css';
import { AudiencePreview } from './AudiencePreview';
import { PlanMultiSelect } from './PlanMultiSelect';

// O editor pesa no bundle e so o admin usa, entao carrega sob demanda.
const AnnouncementEditor = lazy(() => import('./AnnouncementEditor'));

const STATUS_LABELS: Record<AnnouncementStatus, string> = {
    DRAFT: 'Rascunho',
    SENT: 'Enviado',
    RETIRED: 'Retirado',
};

const STATUS_CLASSES: Record<AnnouncementStatus, string> = {
    DRAFT: styles.inactive,
    SENT: styles.active,
    RETIRED: styles.suspended,
};

interface FormState {
    id: string | null;
    status: AnnouncementStatus;
    title: string;
    previewText: string;
    previewDirty: boolean;
    contentHtml: string;
    initialHtml: string;
    planIds: string[];
    signupFrom: string;
    signupTo: string;
    sendEmail: boolean;
    recipients: number;
}

function emptyForm(): FormState {
    return {
        id: null,
        status: 'DRAFT',
        title: '',
        previewText: '',
        previewDirty: false,
        contentHtml: '',
        initialHtml: '',
        planIds: [],
        signupFrom: '',
        signupTo: '',
        sendEmail: false,
        recipients: 0,
    };
}

function formFrom(announcement: Announcement): FormState {
    return {
        id: announcement.id,
        status: announcement.status,
        title: announcement.title,
        previewText: announcement.previewText,
        previewDirty: false,
        contentHtml: announcement.resolvedHtml,
        initialHtml: announcement.resolvedHtml,
        planIds: announcement.planIds,
        signupFrom: announcement.signupFrom?.slice(0, 10) ?? '',
        signupTo: announcement.signupTo?.slice(0, 10) ?? '',
        sendEmail: announcement.sendEmail,
        recipients: announcement.recipients,
    };
}

const formatDate = (value: string) => new Date(value).toLocaleString('pt-BR');
const formatDay = (value: string) => value.slice(0, 10).split('-').reverse().join('/');

function signupLabel(signupFrom: string | null, signupTo: string | null): string {
    if (signupFrom && signupTo) return `cadastro de ${formatDay(signupFrom)} a ${formatDay(signupTo)}`;
    if (signupFrom) return `cadastro desde ${formatDay(signupFrom)}`;
    return signupTo ? `cadastro até ${formatDay(signupTo)}` : '';
}

export function AnnouncementsTab() {
    const [items, setItems] = useState<AnnouncementListItem[]>([]);
    const [plans, setPlans] = useState<AdminPlan[]>([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState<FormState | null>(null);
    // Muda ao abrir outro comunicado, para o editor nascer de novo com aquele conteudo.
    const [editorKey, setEditorKey] = useState(0);
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview] = useState<{ title: string; html: string } | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [announcements, allPlans] = await Promise.all([adminApi.listAnnouncements(), adminApi.listPlans()]);
            setItems(announcements);
            setPlans(allPlans);
        } catch (err) {
            toast((err as Error).message || 'Não foi possível carregar os comunicados.', 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const planNames = useMemo(() => new Map(plans.map((plan) => [plan.id, plan.name])), [plans]);

    const audienceLabel = ({ planIds, signupFrom, signupTo }: AnnouncementRecord) => {
        const plansText =
            planIds.length === 0 ? 'Todos os ativos' : planIds.map((id) => planNames.get(id) ?? 'Plano removido').join(', ');
        const signupText = signupLabel(signupFrom, signupTo);
        return signupText ? `${plansText}, ${signupText}` : plansText;
    };

    const openNew = () => {
        setForm(emptyForm());
        setEditorKey((key) => key + 1);
    };

    const openExisting = async (id: string) => {
        try {
            setForm(formFrom(await adminApi.getAnnouncement(id)));
            setEditorKey((key) => key + 1);
        } catch (err) {
            toast((err as Error).message, 'error');
        }
    };

    const closeForm = () => {
        setForm(null);
        load();
    };

    // Grava o que esta na tela. Todo botao de acao passa por aqui antes, para o
    // teste, a pre-visualizacao e o envio refletirem exatamente o que se ve.
    const save = async (current: FormState): Promise<AnnouncementRecord> => {
        const base = {
            title: current.title,
            contentHtml: current.contentHtml,
            // So manda a previa se o admin mexeu nela: assim a previa padrao
            // continua acompanhando o conteudo.
            ...(current.previewDirty ? { previewText: current.previewText } : {}),
        };
        const payload =
            current.status === 'DRAFT'
                ? {
                      ...base,
                      planIds: current.planIds,
                      signupFrom: current.signupFrom || null,
                      signupTo: current.signupTo || null,
                      sendEmail: current.sendEmail,
                  }
                : base;
        const saved = current.id
            ? await adminApi.updateAnnouncement(current.id, payload)
            : await adminApi.createAnnouncement(payload);
        setForm((prev) =>
            prev && {
                ...prev,
                id: saved.id,
                status: saved.status,
                previewText: saved.previewText,
                previewDirty: false,
            },
        );
        return saved;
    };

    const run = async (action: (current: FormState) => Promise<void>) => {
        if (!form) return;
        if (!form.title.trim()) {
            toast('Informe o título do comunicado.', 'error');
            return;
        }
        setBusy(true);
        try {
            await action(form);
        } catch (err) {
            toast((err as Error).message || 'Não foi possível concluir.', 'error');
        } finally {
            setBusy(false);
        }
    };

    const handleSave = () =>
        run(async (current) => {
            await save(current);
            toast(current.status === 'SENT' ? 'Correção salva para todos os destinatários.' : 'Rascunho salvo.', 'success');
        });

    const handlePreview = () =>
        run(async (current) => {
            const saved = await save(current);
            const full = await adminApi.getAnnouncement(saved.id);
            setPreview({ title: full.title, html: full.resolvedHtml });
        });

    const handleTest = () =>
        run(async (current) => {
            const saved = await save(current);
            await adminApi.sendAnnouncementTest(saved.id);
            toast('Teste enviado. Confira no seu sininho.', 'success');
        });

    const handleSend = () =>
        run(async (current) => {
            const saved = await save(current);
            const { count } = await adminApi.getAnnouncementAudienceCount({
                planIds: saved.planIds,
                signupFrom: current.signupFrom,
                signupTo: current.signupTo,
            });
            const message = `Enviar para ${count} ${count === 1 ? 'usuário' : 'usuários'}?${
                saved.sendEmail ? ' Cada um também recebe o aviso por e-mail.' : ''
            } Depois de enviado, dá para corrigir o texto, mas não para trocar o público.`;
            if (!(await confirmDialog(message, 'Enviar comunicado'))) return;
            const sent = await adminApi.sendAnnouncement(saved.id);
            toast(`Comunicado enviado para ${sent.recipients} usuários.`, 'success');
            closeForm();
        });

    const retire = async (id: string) => {
        const confirmed = await confirmDialog(
            'O comunicado some do sininho de todos e os e-mails pendentes são cancelados. Isso não pode ser desfeito.',
            'Retirar comunicado',
        );
        if (!confirmed) return;
        setBusy(true);
        try {
            await adminApi.retireAnnouncement(id);
            toast('Comunicado retirado.', 'success');
            setForm(null);
            await load();
        } catch (err) {
            toast((err as Error).message, 'error');
        } finally {
            setBusy(false);
        }
    };

    const togglePlan = (planId: string) =>
        setForm((prev) =>
            prev && {
                ...prev,
                planIds: prev.planIds.includes(planId)
                    ? prev.planIds.filter((id) => id !== planId)
                    : [...prev.planIds, planId],
            },
        );

    const previewModal = (
        <Modal isOpen={!!preview} onClose={() => setPreview(null)} title={preview?.title ?? ''} size="xlarge" scrollBody>
            {preview && <RichContent html={preview.html} />}
        </Modal>
    );

    if (form) {
        return (
            <div className={tabStyles.formCard}>
                <div className={tabStyles.formHeader}>
                    <h3>{form.id ? 'Editar comunicado' : 'Novo comunicado'}</h3>
                    <span className={`${styles.statusBadge} ${STATUS_CLASSES[form.status]}`}>{STATUS_LABELS[form.status]}</span>
                </div>

                {form.status === 'SENT' && (
                    <p className={tabStyles.notice}>
                        Já enviado para {form.recipients} usuários. A correção aparece para todos, sem novo aviso e sem marcar
                        como não lido.
                    </p>
                )}

                <Input
                    label="Título"
                    value={form.title}
                    maxLength={120}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    fullWidth
                />

                <div className={tabStyles.field}>
                    <span className={tabStyles.label}>Conteúdo</span>
                    <Suspense fallback={<div className={styles.loadingState}>Carregando editor...</div>}>
                        <AnnouncementEditor
                            key={editorKey}
                            initialHtml={form.initialHtml}
                            onChange={(html) => setForm((prev) => prev && { ...prev, contentHtml: html })}
                            onUploadingChange={setUploading}
                        />
                    </Suspense>
                    <span className={tabStyles.hint}>
                        Imagens, documentos e vídeos enviados ficam privados. Vídeos do YouTube e do Vimeo não: quem tiver o link assiste. Quem recebe também consegue baixar o vídeo enviado.
                    </span>
                </div>

                <div className={tabStyles.field}>
                    <label className={tabStyles.label} htmlFor="announcement-preview">Prévia</label>
                    <textarea
                        id="announcement-preview"
                        className={tabStyles.textarea}
                        rows={2}
                        maxLength={300}
                        value={form.previewText}
                        placeholder="Texto que aparece no sininho e no e-mail. Em branco, usa o começo do conteúdo."
                        onChange={(e) => setForm({ ...form, previewText: e.target.value, previewDirty: true })}
                    />
                </div>

                {form.status === 'DRAFT' && (
                    <>
                        <div className={tabStyles.field}>
                            <span className={tabStyles.label}>Público</span>
                            <div className={tabStyles.audienceFilters}>
                                <div className={tabStyles.field}>
                                    <label className={tabStyles.subLabel} htmlFor="announcement-plans">Planos</label>
                                    <PlanMultiSelect
                                        id="announcement-plans"
                                        plans={plans}
                                        selected={form.planIds}
                                        onToggle={togglePlan}
                                        onClear={() => setForm({ ...form, planIds: [] })}
                                    />
                                </div>
                                <div className={tabStyles.field}>
                                    <label className={tabStyles.subLabel} htmlFor="announcement-signup-from">Cadastro de</label>
                                    <input
                                        id="announcement-signup-from"
                                        type="date"
                                        className={tabStyles.dateInput}
                                        value={form.signupFrom}
                                        max={form.signupTo || undefined}
                                        onChange={(e) => setForm({ ...form, signupFrom: e.target.value })}
                                    />
                                </div>
                                <div className={tabStyles.field}>
                                    <label className={tabStyles.subLabel} htmlFor="announcement-signup-to">Cadastro até</label>
                                    <input
                                        id="announcement-signup-to"
                                        type="date"
                                        className={tabStyles.dateInput}
                                        value={form.signupTo}
                                        min={form.signupFrom || undefined}
                                        onChange={(e) => setForm({ ...form, signupTo: e.target.value })}
                                    />
                                </div>
                            </div>
                            <span className={tabStyles.hint}>
                                Sem plano marcado, vale para todos os planos. Sem data, vale qualquer cadastro. As datas seguem o horário de Brasília.
                            </span>
                            <AudiencePreview planIds={form.planIds} signupFrom={form.signupFrom} signupTo={form.signupTo} />
                        </div>

                        <label className={tabStyles.check}>
                            <input
                                type="checkbox"
                                checked={form.sendEmail}
                                onChange={(e) => setForm({ ...form, sendEmail: e.target.checked })}
                            />
                            Avisar também por e-mail, com o título e a prévia
                        </label>
                    </>
                )}

                <div className={tabStyles.actions}>
                    <Button variant="ghost" onClick={closeForm} disabled={busy || uploading}>Voltar</Button>
                    <div className={tabStyles.actionsRight}>
                        {form.id && form.status !== 'RETIRED' && (
                            <Button variant="outline" onClick={() => retire(form.id as string)} disabled={busy || uploading}>
                                <FiSlash size={15} style={{ marginRight: '0.375rem' }} />Retirar
                            </Button>
                        )}
                        <Button variant="outline" onClick={handlePreview} disabled={busy || uploading}>
                            <FiEye size={15} style={{ marginRight: '0.375rem' }} />Pré-visualizar
                        </Button>
                        <Button variant="outline" onClick={handleTest} disabled={busy || uploading}>Enviar teste para mim</Button>
                        <Button variant={form.status === 'DRAFT' ? 'outline' : 'primary'} onClick={handleSave} disabled={busy || uploading}>
                            {form.status === 'SENT' ? 'Salvar correção' : 'Salvar rascunho'}
                        </Button>
                        {form.status === 'DRAFT' && (
                            <Button onClick={handleSend} disabled={busy || uploading}>
                                <FiSend size={15} style={{ marginRight: '0.375rem' }} />Enviar
                            </Button>
                        )}
                    </div>
                </div>

                {previewModal}
            </div>
        );
    }

    return (
        <div className={styles.usersSection}>
            <div className={styles.sectionHeader}>
                <h3>Comunicados</h3>
                <Button size="small" onClick={openNew}>
                    <FiPlus size={15} style={{ marginRight: '0.375rem' }} />Novo comunicado
                </Button>
            </div>
            {loading ? (
                <div className={styles.loadingState}>Carregando...</div>
            ) : (
                <div className={tabStyles.tableWrap}>
                    <table className={styles.usersTable}>
                        <thead>
                            <tr>
                                <th>Título</th>
                                <th>Status</th>
                                <th>Público</th>
                                <th>Recebidos</th>
                                <th>Lidos</th>
                                <th>E-mails</th>
                                <th>Data</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 ? (
                                <tr><td colSpan={8} className={styles.emptyRow}>Nenhum comunicado criado ainda.</td></tr>
                            ) : (
                                items.map((item) => (
                                    <tr key={item.id}>
                                        <td>{item.title}</td>
                                        <td>
                                            <span className={`${styles.statusBadge} ${STATUS_CLASSES[item.status]}`}>
                                                {STATUS_LABELS[item.status]}
                                            </span>
                                        </td>
                                        <td>{audienceLabel(item)}</td>
                                        <td>{item.status === 'DRAFT' ? '-' : item.recipients}</td>
                                        <td>{item.status === 'SENT' ? item.readCount : '-'}</td>
                                        <td>
                                            {item.sendEmail
                                                ? `${item.emails.SENT ?? 0} enviados, ${item.emails.PENDING ?? 0} na fila${item.emails.FAILED ? `, ${item.emails.FAILED} com erro` : ''}`
                                                : '-'}
                                        </td>
                                        <td>{formatDate(item.sentAt ?? item.createdAt)}</td>
                                        <td>
                                            {item.status !== 'RETIRED' && (
                                                <button
                                                    type="button"
                                                    className={styles.actionBtn}
                                                    title="Editar"
                                                    aria-label="Editar"
                                                    onClick={() => openExisting(item.id)}
                                                >
                                                    <FiEdit2 size={15} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
